import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import type { ContextoAccionHojaRuta } from '../../services/ruta-accion-panel.service';
import type { Productor, Visita } from '../../services/visitas.service';
import type { DetalleAvancePlanNegocio, ProductorPlanNegocioItem } from '../../services/negocio.service';
import {
  ID_ESTADO_PLAN_NEGOCIO,
  ID_ESTADO_SOLICITUD_PLAN,
  normalizarIdNumericoPlanNegocio,
  obtenerIdEstadoSolicitudPlanDesdeProductor,
} from '../../services/negocio.service';
import { RutaDiaStorageService } from '../../services/ruta-dia-storage.service';

@Component({
  selector: 'app-panel-accion-hoja-ruta',
  templateUrl: './panel-accion-hoja-ruta.component.html',
  styleUrls: ['./panel-accion-hoja-ruta.component.scss'],
  standalone: false,
})
export class PanelAccionHojaRutaComponent implements OnChanges {
  @Input({ required: true }) contexto!: ContextoAccionHojaRuta;
  @Input() visible = false;
  @Output() readonly visibleChange = new EventEmitter<boolean>();
  @Output() readonly guardado = new EventEmitter<void>();

  private readonly storage = inject(RutaDiaStorageService);
  private readonly ngZone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  /** Ignora resultados de cargas anteriores si el contexto cambió (evita spinner eterno por solapamiento). */
  private secuenciaCarga = 0;

  cargando = true;
  productor: Productor | undefined;
  plan: ProductorPlanNegocioItem | undefined;
  visita: Visita | undefined;
  /** Evita recalcular en cada ciclo de detección de cambios (datos grandes pueden pegar la UI). */
  filasAvancePlanCache: { etiqueta: string; pct: number }[] = [];

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contexto']) {
      void this.cargarVista();
    }
  }

  get titulo(): string {
    const nombre = this.contexto?.row?.nombreProductor ? `: ${this.contexto.row.nombreProductor}` : '';
    switch (this.contexto.tipo) {
      case 'caracterizacion':
        return `Caracterización${nombre}`;
      case 'plan':
        return `Plan de Negocio${nombre}`;
      case 'registrar':
        return `Registrar Visita${nombre}`;
      case 'detalles':
        return `Ver Detalles${nombre}`;
      default:
        return 'Acción';
    }
  }

  get subtitulo(): string {
    switch (this.contexto.tipo) {
      case 'caracterizacion':
        return 'Datos del usuario INDAP disponibles en la última sincronización (solo lectura).';
      case 'plan':
        return 'Plan de negocio vigente sincronizado (solo lectura).';
      case 'registrar':
        return '';
      case 'detalles':
        return 'Detalle de la visita según datos locales sincronizados (solo lectura).';
      default:
        return '';
    }
  }

  private async cargarVista(): Promise<void> {
    const turno = ++this.secuenciaCarga;
    this.cargando = true;
    this.productor = undefined;
    this.plan = undefined;
    this.visita = undefined;
    this.filasAvancePlanCache = [];
    const row = this.contexto?.row;
    const tipo = this.contexto?.tipo;
    if (tipo === 'registrar' || !row) {
      this.finalizarCargaUi(turno);
      return;
    }
    const timeoutMs = 12000;
    const conTope = <T>(p: Promise<T>): Promise<T> => {
      let t = 0;
      const limite = new Promise<never>((_, rej) => {
        t = window.setTimeout(() => rej(new Error('timeout-idb')), timeoutMs);
      });
      return Promise.race([
        p.finally(() => {
          window.clearTimeout(t);
        }),
        limite,
      ]);
    };

    try {
      if (tipo === 'caracterizacion' && row) {
        this.productor = await conTope(this.storage.obtenerProductorLocal(row.idProductor));
      } else if (tipo === 'plan' && row) {
        await new Promise<void>(resolve => window.setTimeout(() => resolve(), 0));
        const raw = await this.ngZone.runOutsideAngular(() =>
          conTope(this.storage.obtenerPlanLocal(row.idProductor))
        );
        this.aplicarPlanDesdeAlmacenamiento(raw);
      } else if (tipo === 'detalles' && row) {
        this.visita = await conTope(this.storage.obtenerVisitaLocal(row.id));
      }
    } catch {
      this.productor = undefined;
      this.plan = undefined;
      this.visita = undefined;
      this.filasAvancePlanCache = [];
    } finally {
      this.finalizarCargaUi(turno);
    }
  }

  private finalizarCargaUi(turno: number): void {
    if (turno !== this.secuenciaCarga) {
      return;
    }
    this.ngZone.run(() => {
      this.cargando = false;
      this.cdr.detectChanges();
    });
  }

  nombreCompletoProductor(p: Productor | undefined): string {
    if (!p) return '—';
    const parts = [p.nombre, p.primer_apellido, p.segundo_apellido].filter(Boolean);
    return parts.join(' ').trim() || p.nombre || '—';
  }

  /** Etapa del plan según API (null = Diagnóstico, igual criterio que gestión). */
  etiquetaEtapaPlanNegocio(pl: ProductorPlanNegocioItem): string {
    const id = normalizarIdNumericoPlanNegocio(pl.plan_negocio?.id_estado_plan_negocio);
    if (id === ID_ESTADO_PLAN_NEGOCIO.DISENO) return 'Diseño';
    if (id === ID_ESTADO_PLAN_NEGOCIO.EJECUCION) return 'Ejecución';
    return 'Diagnóstico';
  }

  textoEstadoSolicitudPlanResumido(id: number | null): string {
    if (id === ID_ESTADO_SOLICITUD_PLAN.CREADA) return 'Creada (pendiente de resolución)';
    if (id === ID_ESTADO_SOLICITUD_PLAN.APROBADA) return 'Aprobada';
    if (id === ID_ESTADO_SOLICITUD_PLAN.RECHAZADA) return 'Rechazada';
    return 'Sin solicitud registrada en datos locales';
  }

  /** Texto legible con tipo/estado de la última solicitud y estado normalizado. */
  resumenUltimaSolicitudPlan(pl: ProductorPlanNegocioItem): string | null {
    const u = pl.ultima_solicitud;
    const idSol = obtenerIdEstadoSolicitudPlanDesdeProductor(pl);
    const partes: string[] = [];
    if (u?.tipo?.descripcion?.trim()) partes.push(`Tipo: ${u.tipo.descripcion.trim()}`);
    if (u?.estado?.descripcion?.trim()) partes.push(`Estado indicado: ${u.estado.descripcion.trim()}`);
    partes.push(`Solicitud (sistema): ${this.textoEstadoSolicitudPlanResumido(idSol)}`);
    return partes.length ? partes.join(' · ') : null;
  }

  textoCoordenadasPlan(pl: ProductorPlanNegocioItem): string | null {
    const c = pl.coordenadas;
    if (c == null || (c.lat == null && c.lng == null)) return null;
    const lat = Number(c.lat);
    const lng = Number(c.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
  }

  private filasDetalleAvancePlan(pl: ProductorPlanNegocioItem): { etiqueta: string; pct: number }[] {
    const d = pl.detalle_avance;
    if (!d) return [];
    const def: [keyof DetalleAvancePlanNegocio, string][] = [
      ['total', 'Avance general'],
      ['diagnostico', 'Diagnóstico'],
      ['soluciones', 'Soluciones'],
      ['plan_de_trabajo', 'Plan de trabajo'],
      ['plan_de_inversion', 'Plan de inversión'],
      ['margen_bruto', 'Margen bruto'],
    ];
    return def.map(([key, etiqueta]) => ({ etiqueta, pct: this.clampPctAvance(d[key]) }));
  }

  private aplicarPlanDesdeAlmacenamiento(raw: ProductorPlanNegocioItem | undefined): void {
    this.filasAvancePlanCache = [];
    if (!raw) {
      this.plan = undefined;
      return;
    }
    this.plan = this.clonarItemPlanSeguro(raw);
    this.filasAvancePlanCache = this.filasDetalleAvancePlan(this.plan);
  }

  private clonarItemPlanSeguro(p: ProductorPlanNegocioItem): ProductorPlanNegocioItem {
    try {
      if (typeof structuredClone === 'function') {
        return structuredClone(p);
      }
    } catch {
      /* continuar con JSON */
    }
    return JSON.parse(JSON.stringify(p)) as ProductorPlanNegocioItem;
  }

  private clampPctAvance(v: unknown): number {
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(100, Math.round(n)));
  }

  cerrar(): void {
    this.visibleChange.emit(false);
  }

  onRegistrarGuardado(): void {
    this.guardado.emit();
  }
}
